import type { ClientLegalText } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePersonCreate } from '@/features/api/hooks/participant/usePersonCreate';
import { usePersonProfilePictureChange } from '@/features/api/hooks/participant/usePersonProfilePictureChange';
import { useChangeSelectedGeoAreas } from '@/features/api/hooks/shared-app/useChangeSelectedGeoAreas';
import { useAcceptLegalTexts } from '@/features/api/hooks/shared-legal/useAcceptLegalTexts';
import { useUploadTemporaryMediaItem } from '@/features/api/hooks/shared-media/useUploadTemporaryMediaItem';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { useRegistrationStore } from '@/features/authorization/store/useRegistrationStore';
import { AuthStep } from '@/features/authorization/types/step';
import { GeoAreaSubscription } from '@/features/geo-area/components/subscription/GeoAreaSubscription';
import { usePopup } from '@/features/popup/provider/usePopup';
import { Terms } from '@/features/privacy/components/Terms';
import { ProfileEditor } from '@/features/profile-setting/components/ProfileEditor';
import type { PersonUpdate } from '@/features/profile-setting/types/person';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { NavBarType } from '@/navigation/types/navbar';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import { EditMode } from '@/types';

export type Props = AppNativeStackScreenProps<typeof AppRoute.Registration>;

export const RegistrationScreen: FC<Props> = ({ navigation }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.AUTHORIZATION, {
    keyPrefix: 'screens.registration'
  });

  const { logout } = useAuth();
  const { updateAppStates, userEmail, userAuthFirstName, userAuthLastName } =
    useAppState();

  const personCreate = useRegistrationStore((state) => state.personCreate);
  const updatePersonProfile = useRegistrationStore(
    (state) => state.updatePersonProfile
  );
  const updatePersonGeoArea = useRegistrationStore(
    (state) => state.updatePersonGeoArea
  );
  const step = useRegistrationStore((state) => state.step);
  const updateRegistrationStep = useRegistrationStore(
    (state) => state.updateRegistrationStep
  );
  const clearRegistrationStore = useRegistrationStore((state) => state.reset);

  const isInitialStep = step === undefined || step === AuthStep.PROFILE;

  useEffect(() => navigation.setParams({ step }), [navigation, step]);

  const { showAlert } = usePopup();

  const handleCancelRegistration = useCallback(
    () =>
      showAlert({
        labels: t('alert', { returnObjects: true }),
        onConfirm: () => {
          clearRegistrationStore();
          logout();
        }
      }),
    [clearRegistrationStore, logout, showAlert, t]
  );

  const handleBack = useCallback(() => {
    if (isInitialStep) {
      handleCancelRegistration();
    } else {
      updateRegistrationStep('previous');
    }
  }, [handleCancelRegistration, updateRegistrationStep, isInitialStep]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    navigation.setOptions({
      header: () => (
        <NavigationBar
          disableBackButton={isLoading}
          type={
            isInitialStep
              ? NavBarType.MODAL_WITH_CLOSE
              : NavBarType.MODAL_WITH_BACK
          }
          onBackButtonPress={handleBack}
        />
      )
    });
  }, [handleBack, isInitialStep, isLoading, navigation]);

  const handlePersonUpdate = useCallback(
    ({ firstName, lastName, birthYear, localImage }: PersonUpdate) => {
      updatePersonProfile({
        email: userEmail,
        firstName,
        lastName,
        birthYear,
        localImage
      });
    },
    [updatePersonProfile, userEmail]
  );

  const handleHomeGeoAreaUpdate = useCallback(
    (homeAreaId?: string, geoAreaIds?: string[]) => {
      updatePersonGeoArea({ homeAreaId, geoAreaIds });
      if (!isEmptyOrNull(homeAreaId)) updateRegistrationStep();
    },
    [updatePersonGeoArea, updateRegistrationStep]
  );

  const { mutateAsync: createPerson, isError: isCreatePersonError } =
    usePersonCreate();
  const { mutateAsync: accept, isError: isAcceptError } = useAcceptLegalTexts();
  const { mutateAsync: changeGeoAreas, isError: isChangeGeoAreasError } =
    useChangeSelectedGeoAreas();
  const { mutateAsync: uploadTemporaryMediaItem } =
    useUploadTemporaryMediaItem();
  const { mutateAsync: updateProfilePicture } = usePersonProfilePictureChange();

  const handleClearRegistration = useCallback(() => {
    updateAppStates();
    clearRegistrationStore();
  }, [clearRegistrationStore, updateAppStates]);

  const handleRegistration = useCallback(
    async (legalTextIdentifier?: ClientLegalText['legalTextIdentifier'][]) => {
      const legalTextIdentifiersToAccept = legalTextIdentifier?.filter(
        (identifier) => !isEmptyOrNull(identifier)
      );
      if (!personCreate || isEmptyOrNull(personCreate.homeAreaId)) return;

      setIsLoading(true);

      try {
        // UI prevents create button if values are empty
        await createPerson({
          email: personCreate.email ?? '',
          firstName: personCreate.firstName ?? '',
          lastName: personCreate.lastName ?? '',
          birthYear: personCreate.birthYear,
          homeAreaId: personCreate.homeAreaId
        });

        await accept({ legalTextIdentifiersToAccept });
        await changeGeoAreas({
          newSelectedGeoAreaIds: [
            personCreate.homeAreaId,
            ...(personCreate.geoAreaIds ?? [])
          ]
        });

        // Upload image after user is created
        if (personCreate.localImage) {
          const { id } = await uploadTemporaryMediaItem(
            personCreate.localImage
          );
          await updateProfilePicture({ temporaryMediaItemId: id });
        }

        handleClearRegistration();
      } catch (error) {
        handleClearRegistration();
        console.error(
          '[RegistrationScreen]: something went wrong during registration:',
          error
        );
      }
    },
    [
      personCreate,
      createPerson,
      accept,
      changeGeoAreas,
      uploadTemporaryMediaItem,
      updateProfilePicture,
      handleClearRegistration
    ]
  );

  useEffect(() => {
    if (isCreatePersonError || isAcceptError || isChangeGeoAreasError)
      handleClearRegistration();
  }, [
    handleClearRegistration,
    isAcceptError,
    isChangeGeoAreasError,
    isCreatePersonError
  ]);

  if (isLoading)
    return (
      <StateView isLoading backgroundColor={theme.colors.surfaceContainerLow} />
    );

  switch (step) {
    case AuthStep.GEO_AREA: {
      return (
        <GeoAreaSubscription
          selectedGeoAreaId={personCreate?.homeAreaId}
          selectedGeoAreaIds={personCreate?.geoAreaIds}
          onChange={handleHomeGeoAreaUpdate}
          labels={t('geo-area', { returnObjects: true })}
          stickyButton
        />
      );
    }
    case AuthStep.TERMS: {
      return (
        <Terms
          mode={EditMode.REGISTRATION}
          onAccept={handleRegistration}
          stickyButton
        />
      );
    }
    default: {
      return (
        <ProfileEditor
          person={{
            email: userEmail,
            firstName: userAuthFirstName,
            lastName: userAuthLastName,
            ...personCreate
          }}
          mode={EditMode.REGISTRATION}
          onChange={handlePersonUpdate}
          onSave={() => updateRegistrationStep()}
          stickyButton
        />
      );
    }
  }
};
